import { useTheme } from "@emotion/react";
import { alpha, Avatar, Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../provider/authProvider";
import { fileApiRoute } from "../services/networkService";
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import AuthService from "../services/authService";
import { useNavigate } from "react-router-dom";
import Cookies from 'js-cookie';

const Profile = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const { user } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState({});
    const navigate = useNavigate();

    const handleLogout = async () => {
        try {
            setIsLoading(true);
            const token = Cookies.get('refreshToken');

            const data = await AuthService.logout({ token });
            if (data.status === 200) {
                navigate('/');
                Cookies.remove('user');
                Cookies.remove('accessToken');
                Cookies.remove('refreshToken');
            }

        } catch (error) {
            console.error("An unexpected error occurred:", error);
            setError({ general: error.response.data.error });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{
            p: isTablet ? 1 : 4,
            backgroundColor: theme.palette.secondary.main
        }}>
            <Box
                sx={{
                    width: isTablet ? '100%' : '80%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    py: 4,
                    px: isTablet ? 1 : 4,
                }}
            >

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>

                    <Box>
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            {t('name')}: <span style={{ fontWeight: 'bold' }}>{user.name}</span>
                        </Typography>

                        <Typography variant="body1" sx={{ mt: 2 }}>
                            {t('branch')}: <span style={{ fontWeight: 'bold' }}>{user.branch.name}</span>
                        </Typography>

                    </Box>
                </Box>

                <Typography variant="body1" sx={{ mt: 2 }}>
                    {t('phone')}: <span style={{ fontWeight: 'bold' }}>{user.phone}</span>
                </Typography>

                <Typography variant="body1" sx={{ mt: 2 }}>
                    {t('phoneTwo')}: <span style={{ fontWeight: 'bold' }}>{user.phoneTwo}</span>
                </Typography>

                <Typography variant="body1" sx={{ mt: 2 }}>
                    {t('type')}: <span style={{ fontWeight: 'bold' }}>{t(user.type)}</span>
                </Typography>

                {/* role */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        {t('role')}:
                    </Typography>
                    <Box
                        sx={{
                            bgcolor: alpha(
                                user.role.superAdmin ? theme.palette.green.box : theme.palette.purple.box, 0.2),
                            width: 140,
                            justifyContent: 'center',
                            display: 'flex',
                            py: 0.2,
                            borderRadius: 0.8,
                            mt: 2,
                            mx: 1
                        }}
                    >
                        <Box
                            sx={{
                                fontWeight: 'bold',
                                color: user.role.superAdmin ? theme.palette.green.box : theme.palette.purple.box,
                            }}
                        >
                            {user.role.name}
                        </Box>
                    </Box>
                </Box>

                <Typography variant="body1"
                    sx={{
                        mt: 1.5,
                        wordWrap: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        maxHeight: '4.5em',
                        lineHeight: '1.5em'
                    }}>
                    {t('note')}: <span style={{ fontWeight: 'bold' }}>{user.note}</span>
                </Typography>


                {/* status */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        {t('status')}:
                    </Typography>
                    <Box
                        sx={{
                            justifyContent: 'center',
                            display: 'flex',
                            py: 0.2,
                            mt: 2,
                            mx: 0.3
                        }}
                    >
                        <Box
                            sx={{
                                fontWeight: 'bold',
                                color: user.status === 'active' ? theme.palette.green.box : theme.palette.red.box,
                            }}
                        >
                            {t(user.status)}
                        </Box>
                    </Box>
                </Box>


                {/* images */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        mt: 2
                    }}>
                    <Box sx={{ width: '48%' }}>
                        <Typography variant="body1">
                            {t('image')}:
                        </Typography>
                        <Avatar
                            variant="rounded"
                            src={`${fileApiRoute}/${user.image}`}
                            sx={{
                                cursor: 'pointer',
                                width: '100%',
                                height: '180px',
                                mt: 1
                            }}
                        />
                    </Box>

                    <Box sx={{ width: '48%' }}>
                        <Typography variant="body1">
                            {t('IDimage')}:
                        </Typography>
                        <Avatar
                            variant="rounded"
                            src={`${fileApiRoute}/${user.IDImage}`}
                            sx={{
                                cursor: 'pointer',
                                width: '100%',
                                height: '180px',
                                mt: 1
                            }}
                        />
                    </Box>

                </Box>

                {error.general && (
                    <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                        {error.general}
                    </Typography>
                )}


                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    mt: 4
                }}>
                    <Button
                        variant="contained"
                        color="error"
                        sx={{ textTransform: 'none', width: 140, height: 40 }}
                        onClick={() => handleLogout()}
                    >
                        {isLoading ? <CircularProgress color="secondary" size={28} /> :
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <p>{t('logout')}</p>
                                <LogoutOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                            </Box>
                        }
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default Profile;